import React, { useEffect, useRef, useState } from "react";
import { fmt$, fmtPct, fmtX, scopeOptions } from "../lib/model";
import { researchApi, instantlyApi, emailApi, streamPost } from "../lib/api";
import { DealTeaser } from "./TeaserDoc";
import { SequenceEditor, useDealFacts } from "./SequencePicker";
import html2canvas from "html2canvas";
import { base64FromContainer } from "../lib/pdf";
import { jsPDF } from "jspdf";
import { Send, Paperclip, X, Wand2, Scissors, Eraser, Upload, FileText, Users, Share2, ExternalLink, Layers, FolderOpen } from "lucide-react";
import { DocPickerModal } from "./DocPicker";
import { toast } from "sonner";

export const Tile = ({ label, value, sub, tone, testid, small }) => (
  <div className="tile" data-testid={testid}>
    <div className="label">{label}</div>
    <div className={`val ${small ? "val-sm" : ""} ${tone === "go" ? "pos" : tone === "pass" ? "neg" : ""}`}>{value}</div>
    {sub && <div className="sub">{sub}</div>}
  </div>
);

export const Card = ({ title, right, children, className = "", testid, style }) => (
  <div className={`card ${className}`} data-testid={testid} style={style}>
    {title && (
      <div className="card-title">
        <span>{title}</span>
        {right}
      </div>
    )}
    {children}
  </div>
);

export const RecBadge = ({ verdict, testid }) => {
  const tone = verdict === "GO" ? "go" : verdict === "PASS" ? "pass" : "amber";
  const dot = verdict === "GO" ? "go" : verdict === "PASS" ? "pass" : "amber";
  return (
    <span className={`rec ${tone}`} data-testid={testid}>
      {verdict}
    </span>
  );
};

export const KV = ({ k, v, tone }) => (
  <div className="kv-row">
    <span className="k">{k}</span>
    <span className={`v ${tone === "go" ? "pos" : tone === "pass" ? "neg" : ""}`}>{v}</span>
  </div>
);

export const ScenarioBar = ({ value, onChange }) => (
  <div className="scen-bar" data-testid="scenario-bar">
    {["base", "upside", "downside"].map((s) => (
      <button
        key={s}
        className={value === s ? "active" : ""}
        onClick={() => onChange(s)}
        data-testid={`scenario-${s}`}
      >
        {s.charAt(0).toUpperCase() + s.slice(1)}
      </button>
    ))}
  </div>
);

const withCommas = (raw) => {
  const [i, d] = String(raw).replace(/[^0-9.\-]/g, "").split(".");
  const head = i === "" || i === "-" ? i : Number(i).toLocaleString("en-US");
  return d === undefined ? head : `${head}.${d}`;
};

const ValueInput = ({ value, onChange, format, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const ref = useRef(null);
  const display = format ? format(value) : String(value);
  useEffect(() => { if (editing && ref.current) ref.current.select(); }, [editing]);
  const commit = () => {
    const n = parseFloat(String(draft).replace(/[^0-9.\-]/g, ""));
    if (!isNaN(n)) onChange(n);
    setEditing(false);
  };
  return (
    <input
      ref={ref}
      className="cval cval-input mono"
      value={editing ? draft : display}
      onFocus={() => { setEditing(true); setDraft(Number(value) ? withCommas(value) : ""); }}
      onChange={(e) => setDraft(withCommas(e.target.value))}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setEditing(false); e.target.blur(); } }}
      inputMode="decimal"
      style={{ width: `${Math.max(7, (editing ? draft : display).length + 1)}ch` }}
      data-testid={testid ? `${testid}-val` : undefined}
    />
  );
};

export const NumRow = ({ label, value, onChange, format, testid }) => (
  <div className="asmp-row num-row" data-testid={testid}>
    <span className="lbl">{label}</span>
    <ValueInput value={value} onChange={onChange} format={format} testid={testid} />
  </div>
);

export const Slider = ({ label, value, min, max, step, onChange, format, testid }) => {
  const hiMax = value >= max ? value + (max - min || max) : max;
  return (
    <div className="asmp-row">
      <span className="lbl">{label}</span>
      <input
        type="range"
        min={min}
        max={hiMax}
        step={step}
        value={Math.min(Math.max(value, min), hiMax)}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
      />
      <ValueInput value={value} onChange={onChange} format={format} testid={testid} />
    </div>
  );
};

// View scope — this deal only vs. consolidated roll-up vs. a single add-on. Renders nothing when the deal has no add-ons.
// Read-only echo of the deal view chosen on Assumptions — every other page follows that choice.
export const ScopeTag = ({ M, value, onEdit, testid = "scope" }) => {
  const opts = scopeOptions(M);
  if (!opts) return null;
  const label = (opts.find((o) => o.id === value) || opts[0]).label;
  return (
    <span className="scope-echo" data-testid={`${testid}-echo`}>
      <Layers size={12} /> <b>{label}</b>
      {onEdit && <button type="button" className="link-btn lime" onClick={onEdit} data-testid={`${testid}-edit`}>Change on Assumptions</button>}
    </span>
  );
};

export const ScopeBar = ({ M, value, onChange, options, testid = "scope" }) => {
  const opts = options || scopeOptions(M);
  if (!opts) return null;
  return (
    <label className="scope-bar" data-testid={`${testid}-bar`}>
      <Layers size={12} /><span className="sb-lbl">Deal</span>
      <select className="inp cm-scope-sel" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-select`}>
        {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
};

// Dollar input used across the app: "$" sits inside the field, the number carries thousands separators,
// zero renders as an empty field (placeholder) so the figure can be typed directly, and focus selects all.
export const MoneyInput = ({ value, onChange, placeholder = "0", testid, disabled, readOnly, onBlur, style, className = "" }) => {
  const raw = value === "" || value == null ? "" : String(value).replace(/[^0-9.]/g, "").replace(/^0+(?=\d)/, "");
  const [int, dec] = raw.split(".");
  const shown = raw === "" || (Number(int || 0) === 0 && dec === undefined) ? "" : `${Number(int || 0).toLocaleString("en-US")}${dec === undefined ? "" : `.${dec}`}`;
  return (
    <div className={`money-field ${className}`} style={style}>
      <span className="cur">$</span>
      <input className="inp mono" inputMode="decimal" value={shown} placeholder={placeholder} disabled={disabled} readOnly={readOnly}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, "").replace(/\.(?=.*\.)/g, "").replace(/^0+(?=\d)/, ""))} onBlur={onBlur} data-testid={testid} />
    </div>
  );
};

// Percent input: "%" suffix inside the field, no leading zeros, focus selects all.
export const PctInput = ({ value, onChange, placeholder = "0", testid, disabled, style, className = "" }) => {
  const raw = value === "" || value == null ? "" : String(value).replace(/[^0-9.]/g, "").replace(/^0+(?=\d)/, "");
  return (
    <div className={`pct-field ${className}`} style={style}>
      <input className="inp mono" inputMode="decimal" value={raw} placeholder={placeholder} disabled={disabled}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, "").replace(/\.(?=.*\.)/g, "").replace(/^0+(?=\d)/, ""))} data-testid={testid} />
      <span className="sfx">%</span>
    </div>
  );
};



export const Computed = ({ label, value, tone, testid, compact = false }) => (
  <div className={`asmp-computed${compact ? " compact" : ""}`} data-testid={testid}>
    <span style={{ color: "var(--text2)" }}>{label}</span>
    <span className={`cval ${tone === "go" ? "pos" : tone === "pass" ? "neg" : ""}`}>{value}</span>
  </div>
);

export const toneClass = (t) => (t === "go" ? "pos" : t === "pass" ? "neg" : t === "amber" ? "neu" : "");
export { fmt$, fmtPct, fmtX };

// Small external-link icon next to a company name: opens its website, or a web search when no URL is known.
export const SiteLink = ({ url, name, testid }) => {
  const u = (url || "").trim();
  if (!u && !name) return null;
  const href = u ? (/^https?:\/\//i.test(u) ? u : `https://${u}`) : `https://www.google.com/search?q=${encodeURIComponent(name)}`;
  return (
    <a className="site-link" href={href} target="_blank" rel="noreferrer" title={u ? `Open ${u}` : `Search the web for ${name}`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <ExternalLink size={11} />
    </a>
  );
};

export const useResearch = (kind) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setLoading(true);
    try { setResult(await researchApi.entities({ kind, query, location, count: 30 })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Web search failed"); }
    finally { setLoading(false); }
  };
  const findMore = async () => {
    if (!result) return run();
    setLoading(true);
    try {
      const exclude = (result.entities || []).map((e) => e.name).filter(Boolean);
      const r = await researchApi.entities({ kind, query, location, count: 30, exclude });
      const names = new Set(exclude.map((n) => n.toLowerCase()));
      const more = (r.entities || []).filter((e) => e.name && !names.has(e.name.toLowerCase()));
      if (!more.length) { toast.info("No additional results found"); return; }
      const cites = [...(result.citations || [])];
      const seenU = new Set(cites.map((c) => c.url));
      (r.citations || []).forEach((c) => { if (!seenU.has(c.url)) { cites.push(c); seenU.add(c.url); } });
      setResult({ entities: [...result.entities, ...more], citations: cites });
      toast.success(`Added ${more.length} more`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Web search failed"); }
    finally { setLoading(false); }
  };
  const start = (q = "", loc = "") => { setQuery(q); setLocation(loc); setResult(null); setOpen(true); };
  return { open, setOpen, query, setQuery, location, setLocation, loading, result, run, findMore, start };
};

export const Logo = ({ height = 36, className = "", testid }) => (
  <span className={`logo-badge ${className}`} style={{ height, padding: Math.round(height * 0.1) }} data-testid={testid}>
    <img src="/xyz-logo.png" alt="XYZ Ventures" style={{ height: "100%" }} />
  </span>
);


export const ConfirmModal = ({ open, title, message, confirmLabel = "Confirm", danger, onConfirm, onCancel, testid = "confirm-modal" }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay" data-testid={testid} onMouseDown={onCancel}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-title">{title}</div>
        {message && <div className="modal-sub">{message}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onCancel} data-testid="modal-cancel">Cancel</button>
          <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} onClick={onConfirm} data-testid="modal-confirm">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

export const TamFunnel = ({ tam, testid = "tam-funnel" }) => {
  if (!tam) return null;
  const rows = [
    ["TAM", tam.value, "Total addressable", 100],
    ["SAM", tam.sam, "Serviceable", 66],
    ["SOM", tam.som, "Obtainable", 36],
  ].filter((r) => r[1]);
  if (!rows.length) return null;
  return (
    <div className="tam-funnel" data-testid={testid}>
      {rows.map(([k, v, sub, w]) => (
        <div className="tf-row" key={k}>
          <div className="tf-bar" style={{ width: `${w}%` }}>
            <span className="tf-k">{k}</span>
            <span className="tf-v">{v}</span>
          </div>
          <span className="tf-sub">{sub}{k === "TAM" && tam.cagr ? ` · ${tam.cagr}` : ""}</span>
        </div>
      ))}
    </div>
  );
};

// Reusable Perplexity web-research modal. `describe(e)` -> JSX summary; `onSave(e)` -> persist. `subtitle` optional.
export const ResearchModal = ({ open, onClose, title, subtitle, kind, query, setQuery, location, setLocation, run, findMore, loading, result, describe, onSave, savedKey }) => {
  const [saved, setSaved] = React.useState({});
  const [savingAll, setSavingAll] = React.useState(false);
  React.useEffect(() => { if (open) setSaved({}); }, [open]);
  if (!open) return null;
  const key = savedKey || ((e, i) => `${e.name || i}-${i}`);
  const doSave = async (e, i) => { await onSave(e); setSaved((s) => ({ ...s, [key(e, i)]: true })); };
  const count = result?.entities?.length || 0;
  const savedCount = Object.values(saved).filter(Boolean).length;
  const saveAll = async () => {
    if (!count) return;
    setSavingAll(true);
    const tid = toast.loading(`Saving ${count - savedCount} result(s) to your CRM…`);
    let n = 0;
    try {
      for (let i = 0; i < result.entities.length; i++) {
        const e = result.entities[i];
        if (saved[key(e, i)]) continue;
        try { await doSave(e, i); n++; } catch { /* skip one */ }
      }
      toast.success(`Saved ${n} to your CRM`, { id: tid });
    } finally { setSavingAll(false); }
  };
  return (
    <div className="modal-overlay" data-testid="research-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 720, width: "94%", maxHeight: "88vh", overflowY: "auto" }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-title">{title}</div>
        <div className="modal-sub">{subtitle || "Live web search via Perplexity — every result carries source citations. Verify before outreach; nothing is saved until you click Save."}</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          <input className="inp" style={{ flex: 2, minWidth: 180 }} placeholder="Sector / criteria" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !loading) run(); }} data-testid="research-query" />
          {setLocation && <input className="inp" style={{ flex: 1, minWidth: 120 }} placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="research-location" />}
          <button className="btn btn-primary" onClick={run} disabled={loading} data-testid="research-run">{loading ? "Searching…" : "Search web"}</button>
        </div>
        {loading && count === 0 && <div className="bullet">Researching the web across public sources… this can take 10-25s for a large list.</div>}
        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {count === 0 && !loading && <div style={{ fontSize: 12, color: "var(--text3)" }}>No results parsed — try refining the sector or location.</div>}
            {count > 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, color: "var(--text2)" }} data-testid="research-count"><b>{count}</b> live web result{count === 1 ? "" : "s"}{savedCount ? ` · ${savedCount} saved` : ""}</div>
                <button className="btn btn-primary btn-sm" onClick={saveAll} disabled={savingAll || savedCount >= count} data-testid="research-save-all">{savingAll ? "Saving…" : savedCount >= count ? "All saved ✓" : `Save all (${count - savedCount})`}</button>
              </div>
            )}
            {result.entities.map((e, i) => (
              <div key={i} className="banner info" style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }} data-testid={`research-item-${i}`}>
                <div style={{ flex: 1, minWidth: 0 }}>{describe(e)}</div>
                <button className="btn btn-sm" onClick={() => doSave(e, i)} disabled={!!saved[key(e, i)]} data-testid={`research-save-${i}`}>{saved[key(e, i)] ? "Saved ✓" : "Save"}</button>
              </div>
            ))}
            {count > 0 && findMore && (
              <button className="btn" onClick={findMore} disabled={loading} style={{ alignSelf: "center", marginTop: 2 }} data-testid="research-find-more">{loading ? "Finding more…" : "Find more results"}</button>
            )}
            {result.citations?.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text2)", marginBottom: 4 }}>Sources</div>
                {result.citations.map((c, i) => (
                  <a key={i} href={c.url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 11, color: "var(--text2)" }} data-testid={`research-src-${i}`}>{i + 1}. {c.title}</a>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="modal-actions"><button className="btn" onClick={onClose} data-testid="research-close">Close</button></div>
      </div>
    </div>
  );
};


// Instantly outreach — push selected contacts into an Instantly email campaign (existing or new).
export const useOutreach = () => {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [defaultName, setDefaultName] = useState("");
  const [source, setSource] = useState("");
  const start = (list, dName = "", src = "") => { setContacts(list || []); setDefaultName(dName); setSource(src); setOpen(true); };
  return { open, setOpen, contacts, defaultName, source, start };
};

export const AddToCampaignModal = ({ open, onClose, contacts = [], defaultGroupName = "", source = "", dealKey = "", dealName = "" }) => {
  const facts = useDealFacts();
  const [campaigns, setCampaigns] = React.useState(null);
  const [mode, setMode] = React.useState("new");
  const [campaignId, setCampaignId] = React.useState("");
  const [newName, setNewName] = React.useState("");
  const [seq, setSeq] = React.useState({ template: "", steps: [] });
  const [syncing, setSyncing] = React.useState(false);
  const withEmail = contacts.filter((c) => c.email).length;

  React.useEffect(() => {
    if (!open) return;
    setNewName(defaultGroupName || "XYZ Ventures outreach");
    setCampaigns(null); setMode("new"); setCampaignId(""); setSeq({ template: "", steps: [] });
    instantlyApi.campaigns()
      .then((r) => setCampaigns(r.campaigns || []))
      .catch((e) => { setCampaigns([]); toast.error(e?.response?.data?.detail || "Could not load Instantly campaigns"); });
  }, [open, defaultGroupName]);

  if (!open) return null;

  const run = async () => {
    if (mode === "existing" && !campaignId) return toast.error("Pick a campaign");
    if (mode === "new" && !newName.trim()) return toast.error("Enter a campaign name");
    const list = contacts.filter((c) => c.email);
    if (!list.length) return toast.error("None of these contacts have an email address");
    setSyncing(true);
    const tid = toast.loading(`Adding ${list.length.toLocaleString()} contact(s) to Instantly…`);
    // Chunk client-side: one giant request (thousands of contacts) exceeds the gateway body/time
    // limit and 502s. Small batches also let us show progress and keep each call fast.
    const CHUNK = 500;
    let cid = mode === "existing" ? campaignId : "";
    let cname = mode === "existing" ? ((campaigns || []).find((c) => c.id === campaignId)?.name || "") : newName.trim();
    const totals = { added: 0, skipped: 0, duplicates: 0, invalid: 0 };
    try {
      for (let i = 0; i < list.length; i += CHUNK) {
        const batch = list.slice(i, i + CHUNK);
        const payload = cid
          ? { campaign_id: cid, campaign_name: cname, contacts: batch, dealKey, dealName, source }
          : { campaign_name: cname, contacts: batch, dealKey, dealName, source, steps: seq.steps, template: seq.template };
        const res = await instantlyApi.addLeads(payload);
        cid = res.campaign_id || cid;        // reuse the campaign for every later batch (never recreate)
        cname = res.campaign_name || cname;
        totals.added += res.added || 0;
        totals.skipped += res.skipped || 0;
        totals.duplicates += res.duplicates || 0;
        totals.invalid += res.invalid || 0;
        if (list.length > CHUNK) toast.loading(`Adding to “${cname}”… ${Math.min(i + CHUNK, list.length).toLocaleString()} of ${list.length.toLocaleString()}`, { id: tid });
      }
      toast.success(`${totals.added.toLocaleString()} added to “${cname}”${totals.skipped ? ` · ${totals.skipped.toLocaleString()} skipped` : ""}${mode === "new" && seq.steps.length ? ` · ${seq.steps.length}-email sequence loaded` : ""} — replies land in your Outreach Inbox`, { id: tid });
      onClose();
    } catch (e) {
      toast.error(`${e?.response?.data?.detail || "Instantly upload failed"}${totals.added ? ` (added ${totals.added.toLocaleString()} before the error)` : ""}`, { id: tid });
    } finally { setSyncing(false); }
  };

  const wide = mode === "new" && seq.steps.length > 0;
  return (
    <div className="modal-overlay" data-testid="instantly-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: wide ? 760 : 520, width: "94%", maxHeight: "92vh", overflowY: "auto" }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-title">Add to Instantly campaign</div>
        <div className="modal-sub">Load these contacts into an Instantly email sequence. {contacts.length.toLocaleString()} contact(s) · {withEmail.toLocaleString()} with email. Replies come back in the Outreach Inbox.</div>

        <div style={{ display: "flex", gap: 16, margin: "6px 0 12px" }}>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, cursor: "pointer" }}>
            <input type="radio" checked={mode === "new"} onChange={() => setMode("new")} data-testid="camp-mode-new" /> New campaign
          </label>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, cursor: "pointer" }}>
            <input type="radio" checked={mode === "existing"} onChange={() => setMode("existing")} data-testid="camp-mode-existing" /> Existing campaign
          </label>
        </div>

        {mode === "new" ? (
          <>
            <div className="field"><label>New campaign name</label>
              <input className="inp" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Project Cypress — LP raise" data-testid="camp-new-name" />
            </div>
            <div className="section-label" style={{ marginTop: 6 }}>Start from a sequence</div>
            <SequenceEditor facts={facts} value={seq} onChange={setSeq} compact defaultTemplate={source === "offmarket" ? "seller" : source === "bizdev" ? "b2b" : ""} />
          </>
        ) : (
          <div className="field"><label>Choose a campaign</label>
            {campaigns === null ? <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading campaigns…</div> : (
              <select className="inp" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} data-testid="camp-select">
                <option value="">Select a campaign…</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
        )}

        <div className="cm-empty">New campaigns are created paused with your business-hours schedule{seq.steps.length ? " and the sequence above" : " — write the sequence"}; hit start inside Instantly.</div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose} data-testid="camp-cancel">Cancel</button>
          <button className="btn btn-primary" onClick={run} disabled={syncing || !contacts.length} data-testid="camp-add-go">{syncing ? "Adding…" : `Add ${withEmail.toLocaleString()} to campaign`}</button>
        </div>
      </div>
    </div>
  );
};

// ---------------- Reusable rich email composer (Investors / Lenders / NNN / Placement) ----------------
const SENDER = { name: "XYZ Ventures", email: "ike@xyzvtr.com" };
const stripSubject = (text) => (text || "").replace(/^subject:.*(\r?\n)+/i, "").trim();
const grabSubject = (text, fallback) => { const mt = (text || "").match(/subject:\s*(.*)/i); return mt ? mt[1].trim() : fallback; };

export const useCompose = () => {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const start = (cfg) => { setConfig(cfg); setOpen(true); };
  return { open, setOpen, config, start };
};

export const ComposeEmailModal = ({ open, onClose, config }) => {
  const cfg = config || {};
  const [recips, setRecips] = React.useState([]);
  const [subject, setSubject] = React.useState("");
  const [body, setBody] = React.useState("");
  const [attachments, setAttachments] = React.useState([]);
  const [drafting, setDrafting] = React.useState(false);
  const [shortening, setShortening] = React.useState(false);
  const [rewrite, setRewrite] = React.useState("");
  const [attachingTeaser, setAttachingTeaser] = React.useState(false);
  const [docPicker, setDocPicker] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const bodyRef = React.useRef(null);
  const fileRef = React.useRef(null);
  const hiddenTeaserRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    setRecips((cfg.recipients || []).map((r) => ({ name: r.name || "", email: r.email || "" })));
    setSubject(cfg.subject || "");
    setBody(cfg.body || "");
    setAttachments([]);
    setRewrite("");
  }, [open]); // eslint-disable-line

  if (!open) return null;
  const single = recips.length <= 1;
  const withEmail = recips.filter((r) => r.email).length;

  const insertVar = (val) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + val); return; }
    const s = el.selectionStart ?? body.length, e = el.selectionEnd ?? body.length;
    setBody(body.slice(0, s) + val + body.slice(e));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + val.length; });
  };

  const draftAI = async () => {
    if (!cfg.draftEndpoint) return;
    setDrafting(true);
    let full = "";
    try {
      await streamPost(cfg.draftEndpoint, cfg.draftPayload || {}, (c, f) => { full = f; setBody(stripSubject(f)); });
      setSubject((s) => grabSubject(full, s));
      toast.success("Draft written");
    } catch { toast.error("Draft failed"); }
    finally { setDrafting(false); }
  };

  const shorten = async (instrOverride) => {
    if (!body.trim()) return toast.error("Write or draft a message first");
    const instr = (instrOverride !== undefined ? instrOverride : rewrite).trim();
    setShortening(true);
    try {
      await streamPost("/ai/shorten", { text: body, instruction: instr || null }, (c, f) => setBody(f));
      toast.success(instr ? "Rewritten to your instruction" : "Shortened");
    } catch { toast.error("Could not rewrite"); }
    finally { setShortening(false); }
  };

  const attachTeaser = async () => {
    // Multi-page documents (investor / lender packages) hand us the container they render into.
    if (typeof cfg.pdfContainer === "function") {
      const node = cfg.pdfContainer();
      if (!node) return toast.error("Document not ready");
      setAttachingTeaser(true);
      try {
        const { content, pages } = await base64FromContainer(node);
        const filename = cfg.teaserFilename || `XYZ-Ventures-${(cfg.dealName || "deal").replace(/\s+/g, "-")}.pdf`;
        setAttachments((a) => [...a.filter((x) => x.filename !== filename), { filename, content, label: cfg.attachLabel || "Document", size: Math.round(content.length * 0.75) }]);
        toast.success(`${cfg.attachLabel || "Document"} attached (${pages} pages)`);
      } catch (e) { console.error("[pdf]", e); toast.error(`Could not build the PDF — ${e.message || e}`); }
      finally { setAttachingTeaser(false); }
      return;
    }
    const node = (cfg.teaserRef && cfg.teaserRef.current) || hiddenTeaserRef.current;
    if (!node) return toast.error("Teaser not ready");
    setAttachingTeaser(true);
    try {
      const orientation = cfg.teaserOrientation || "portrait";
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, windowWidth: node.scrollWidth });
      const img = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation });
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      if (orientation === "landscape") {
        const imgH = (canvas.height * pw) / canvas.width; let pos = 0;
        pdf.addImage(img, "JPEG", 0, pos, pw, imgH); let rem = imgH - ph;
        while (rem > 0) { pos -= ph; pdf.addPage(); pdf.addImage(img, "JPEG", 0, pos, pw, imgH); rem -= ph; }
      } else {
        const ratio = Math.min(pw / canvas.width, ph / canvas.height);
        pdf.addImage(img, "JPEG", (pw - canvas.width * ratio) / 2, 0, canvas.width * ratio, canvas.height * ratio);
      }
      const content = pdf.output("datauristring").split(",")[1];
      const filename = cfg.teaserFilename || `XYZ-Ventures-Teaser-${(cfg.dealName || "deal").replace(/\s+/g, "-")}.pdf`;
      setAttachments((a) => [...a.filter((x) => x.filename !== filename), { filename, content, label: "Teaser", size: Math.round(content.length * 0.75) }]);
      toast.success("Teaser attached");
    } catch { toast.error("Could not build teaser"); }
    finally { setAttachingTeaser(false); }
  };

  const onUpload = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",")[1] || "";
      setAttachments((a) => [...a, { filename: file.name, content: b64, label: file.name, size: file.size }]);
      toast.success(`Attached ${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  const send = async () => {
    const to = recips.filter((r) => r.email).map((r) => ({ name: r.name, email: r.email }));
    if (!to.length) return toast.error("Add at least one recipient with an email");
    if (!subject.trim()) return toast.error("Add a subject");
    if (!body.trim() && !attachments.length) return toast.error("Write a message or attach a document");
    setSending(true);
    try {
      const res = await emailApi.compose({ to, subject, body, attachments: attachments.map(({ filename, content }) => ({ filename, content })), deal_name: cfg.dealName || "", kind: cfg.kind || "outreach", room_id: cfg.roomId || "" });
      if (res.mock) toast.warning(`Recorded ${res.sent} recipient(s) — no live email (sender config)`);
      else toast.success(`Sent to ${res.delivered}/${res.sent} recipient(s)${attachments.length ? ` with ${attachments.length} attachment(s)` : ""} via Resend`);
      cfg.onSent?.(to, res);
      onClose();
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="modal-overlay" data-testid="compose-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 760, width: "96%", maxHeight: "92vh", overflowY: "auto" }} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div className="modal-title" style={{ marginBottom: 0 }}>{cfg.title || "Compose Email"}</div>
          <button className="btn btn-sm" onClick={onClose} data-testid="compose-close"><X size={14} /></button>
        </div>
        <div className="cmp-row"><span className="cmp-k">From</span><span className="cmp-from">{SENDER.name} <span className="mono" style={{ color: "var(--text3)" }}>{SENDER.email}</span></span></div>

        {single ? (
          <div className="cmp-row">
            <span className="cmp-k">To</span>
            <div style={{ display: "flex", gap: 8, flex: 1 }}>
              <input className="inp" style={{ flex: "0 0 40%" }} placeholder="Name" value={recips[0]?.name || ""} onChange={(e) => setRecips([{ name: e.target.value, email: recips[0]?.email || "" }])} data-testid="compose-to-name" />
              <input className="inp" style={{ flex: 1 }} placeholder="name@firm.com" value={recips[0]?.email || ""} onChange={(e) => setRecips([{ name: recips[0]?.name || "", email: e.target.value }])} data-testid="compose-to-email" />
            </div>
          </div>
        ) : (
          <div className="cmp-row" style={{ alignItems: "flex-start" }}>
            <span className="cmp-k"><Users size={12} style={{ marginRight: 4 }} />To</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 6 }} data-testid="compose-recip-count">{recips.length} recipients · {withEmail} with email</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 92, overflowY: "auto" }}>
                {recips.map((r, i) => (
                  <span key={i} className="attach-chip" style={{ fontSize: 11 }} data-testid={`compose-recip-${i}`}>
                    {r.name || r.email}{!r.email && <span style={{ color: "#e66" }}> (no email)</span>}
                    <button className="btn" style={{ padding: "0 4px" }} onClick={() => setRecips(recips.filter((_, j) => j !== i))} data-testid={`compose-recip-remove-${i}`}><X size={11} /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="cmp-row"><span className="cmp-k">Subject</span><input className="inp inp-text" style={{ flex: 1 }} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" /></div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "10px 0 6px" }}>
          {cfg.draftEndpoint && <button className="btn" onClick={draftAI} disabled={drafting} data-testid="compose-draft-ai"><Wand2 size={13} style={{ marginRight: 4 }} />{drafting ? "Drafting…" : "Draft with AI"}</button>}
          <button className="btn" onClick={() => shorten("")} disabled={shortening || !body.trim()} data-testid="compose-shorten"><Scissors size={13} style={{ marginRight: 4 }} />{shortening ? "Working…" : "Shorten"}</button>
          <button className="btn" onClick={() => setBody("")} disabled={!body.trim()} data-testid="compose-clear"><Eraser size={13} style={{ marginRight: 4 }} />Clear</button>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
          <Wand2 size={13} style={{ color: "var(--text3)", flex: "0 0 auto" }} />
          <input className="inp" style={{ flex: 1 }} placeholder={'AI rewrite — e.g. "shorten to 3 sentences" or "make it warmer"'} value={rewrite} onChange={(e) => setRewrite(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); shorten(); } }} data-testid="compose-rewrite" />
          <button className="btn btn-primary" onClick={() => shorten()} disabled={shortening || !body.trim() || !rewrite.trim()} data-testid="compose-rewrite-apply">{shortening ? "…" : "Apply"}</button>
        </div>

        <textarea ref={bodyRef} className="inp" rows={11} style={{ resize: "vertical", fontFamily: "inherit" }} placeholder="Write your message, or use Draft with AI…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="compose-body" />

        {(cfg.vars && cfg.vars.length > 0) && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
            <span style={{ fontSize: 10.5, color: "var(--text3)" }}>Insert:</span>
            {cfg.vars.map(([label, val]) => <button key={label} className="chip" onClick={() => insertVar(String(val))} data-testid={`compose-var-${String(label).replace(/\s+/g, "-").toLowerCase()}`}>{label}</button>)}
          </div>
        )}

        <div className="section-label" style={{ marginTop: 14 }}>Attachments</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {(cfg.pdfContainer || cfg.teaserRef || cfg.M) && <button className="btn btn-primary" onClick={attachTeaser} disabled={attachingTeaser} data-testid="compose-attach-teaser"><FileText size={13} style={{ marginRight: 4 }} />{attachingTeaser ? "Building…" : `Attach ${cfg.attachLabel || "Teaser"}`}</button>}
          <button className="btn" onClick={() => setDocPicker(true)} data-testid="compose-attach-docs"><FolderOpen size={13} style={{ marginRight: 4 }} />From Docs Room</button>
          <input ref={fileRef} type="file" style={{ display: "none" }} onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} data-testid="compose-file-input" />
          <button className="btn" onClick={() => fileRef.current?.click()} data-testid="compose-upload"><Upload size={13} style={{ marginRight: 4 }} />Upload File</button>
        </div>
        {attachments.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {attachments.map((a) => (
              <div key={a.filename} className="attach-chip" data-testid="compose-attach-chip">
                <Paperclip size={13} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                <span style={{ color: "var(--text3)", fontSize: 10.5 }}>{(a.size / 1024).toFixed(1)} KB</span>
                <button className="btn" style={{ padding: "2px 6px" }} onClick={() => setAttachments((x) => x.filter((y) => y.filename !== a.filename))} data-testid="compose-attach-remove"><X size={13} /></button>
              </div>
            ))}
          </div>
        ) : <div style={{ fontSize: 11, color: "var(--text3)" }}>No attachments. Pull a teaser, deck, financials or tax return from the Docs Room, build the branded PDF, or upload your own.</div>}

        <DocPickerModal open={docPicker} onClose={() => setDocPicker(false)} dealKey={cfg.dealKey}
          onAttach={(files) => setAttachments((a) => [...a.filter((x) => !files.some((f) => f.filename === x.filename)), ...files])} />

        <button className="btn btn-primary" style={{ width: "100%", marginTop: 14 }} onClick={send} disabled={sending} data-testid="compose-send"><Send size={13} style={{ marginRight: 6 }} />{sending ? "Sending…" : `Send Email${recips.length > 1 ? ` (${withEmail})` : ""}`}</button>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>Sent live via Resend from {SENDER.email}. Attachments delivered as files.</div>

        {!cfg.teaserRef && cfg.M && (
          <div style={{ position: "fixed", left: -99999, top: 0, width: 820, pointerEvents: "none" }} aria-hidden>
            <DealTeaser ref={hiddenTeaserRef} M={cfg.M} displayName={cfg.teaserName} industry={cfg.teaserIndustry} location={cfg.teaserLocation} />
          </div>
        )}
      </div>
    </div>
  );
};

